'use client'

import React, { useState } from 'react'
import type { Event, Media, Tag, EventType } from '@/payload-types'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import { Label } from '@/ui/atoms/Label'
import { Dialog } from '@/ui/components/Dialog'
import { EventModal } from '@/ui/components/Card/EventCard/EventModal'
import type { TypedLocale } from 'payload'

type AuthorData = {
  name: string
  image?: Media | string | null
  verified?: boolean
}

interface CalendarProps {
  events: Event[]
  showTitle?: boolean
  locale?: TypedLocale
  /** Short day cells + tighter chrome (Events page "Month view", 2026-09). */
  compact?: boolean
}

export function Calendar({ events, showTitle = false, locale, compact = false }: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    // Convert Sunday (0) to 7, and shift to Monday-first (1-7 becomes 0-6)
    return firstDay === 0 ? 6 : firstDay - 1
  }

  const getEventsForDay = (day: number) => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const dateStrRaw = new Date(Date.UTC(year, month, day)).toISOString().split('T')[0]
    const dateStr = dateStrRaw ?? ''

    return events.filter((event) => {
      if (!event.startDate) return false
      const startDate = event.startDate
      if (!startDate) return false

      const eventStartDateRaw = startDate.split('T')[0]
      const eventStartDate = eventStartDateRaw ?? ''

      const eventEndDateRaw = event.endDate ? event.endDate.split('T')[0] : eventStartDateRaw
      const eventEndDate = eventEndDateRaw ?? ''

      if (!dateStr || !eventStartDate || !eventEndDate) return false

      return dateStr >= eventStartDate && dateStr <= eventEndDate
    })
  }

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }

  const daysInMonth = getDaysInMonth(currentDate)
  const firstDayOfMonth = getFirstDayOfMonth(currentDate)

  const calendarDays = []
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null)
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day)
  }

  return (
    <div>
      {showTitle && (
        <h2 className="text-headline-M-semibold lg:mb-section mb-mobile-section">Calendar</h2>
      )}
      <div className={compact ? 'border border-subtle rounded-md p-3' : 'border border-subtle rounded-md p-4'}>
        {/* Month Navigation */}
        <div className={compact ? 'flex items-center justify-between mb-4' : 'flex items-center justify-between mb-16'}>
          <Button
            variant="secondary"
            size="md"
            onClick={handlePrevMonth}
            aria-label="Previous month"
          >
            <ButtonContent showIconLeft iconLeft="arrow-left-line" />
          </Button>
          <span className="text-text-base text-headline-S-semibold">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <Button variant="secondary" size="md" onClick={handleNextMonth} aria-label="Next month">
            <ButtonContent showIconRight iconRight="arrow-right-line" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-[8px] relative pb-8 lg:pb-0">
          {/* Day Headers */}
          {dayNames.map((day) => (
            <div key={day} className="text-start text-headline-XS-regular text-text-subtle py-2">
              {day}
            </div>
          ))}

          {/* Calendar Days */}
          {calendarDays.map((day, index) => {
            const dayEvents = day ? getEventsForDay(day) : []
            const hasEvents = dayEvents.length > 0

            return day ? (
              <div
                key={index}
                className={
                  compact
                    ? 'bg-bg-subtle-blue border border-bg-subtle-blue rounded-md w-full min-h-[52px] md:min-h-[64px] p-1.5 md:p-2 text-sm relative overflow-visible group'
                    : 'bg-bg-subtle-blue border border-bg-subtle-blue rounded-md w-full aspect-square p-2 text-sm relative overflow-visible group'
                }
              >
                <div className="md:text-title-S-medium text-label-XS-semibold text-text-subtle mb-1">
                  {day}
                </div>
                {/* Event labels for md and larger screens */}
                <div className="hidden md:block space-y-1 overflow-visible">
                  {dayEvents.slice(0, 3).map((event, eventIndex) => {
                    const isTruncatedMd = event.title && event.title.length > 15
                    const isTruncatedLg = event.title && event.title.length > 30
                    const displayTitle = isTruncatedMd
                      ? `${event.title.slice(0, 15)}...`
                      : event.title
                    const displayTitleLg = isTruncatedLg
                      ? `${event.title.slice(0, 30)}...`
                      : event.title
                    const shouldHideOnMedium = dayEvents.length > 1 && eventIndex > 0
                    // Colour = event type (same scheme as the cards' type chip); blue fallback.
                    const et = event.eventType as EventType | string | null | undefined
                    const scheme = et && typeof et === 'object' ? et.colorScheme : undefined
                    const chipClass = `w-full max-w-full !justify-start [&>span]:truncate cursor-pointer flex${scheme ? '' : ' [&>span]:text-text-accent'}`

                    return (
                      <div key={eventIndex} className="relative">
                        {/* Clickable label for md screens - show only first label */}
                        <button
                          onClick={() => setSelectedEvent(event)}
                          className={`w-full lg:hidden ${shouldHideOnMedium ? 'hidden' : 'md:flex'}`}
                          type="button"
                          title={event.title}
                        >
                          <Label
                            title={displayTitle}
                            variant={scheme ? undefined : 'blue'}
                            colorScheme={scheme}
                            size="small"
                            className={chipClass}
                          />
                        </button>
                        {/* Clickable label for lg+ screens - show all labels */}
                        <button
                          onClick={() => setSelectedEvent(event)}
                          className="w-full hidden lg:flex"
                          type="button"
                          title={event.title}
                        >
                          <Label
                            title={displayTitleLg}
                            variant={scheme ? undefined : 'blue'}
                            colorScheme={scheme}
                            size="small"
                            className={chipClass}
                          />
                        </button>
                      </div>
                    )
                  })}
                </div>

                {/* Blue circle indicator for mobile screens only */}
                {hasEvents && (
                  <button
                    onClick={() => dayEvents[0] && setSelectedEvent(dayEvents[0])}
                    className="md:hidden absolute bottom-1 left-1/2 -translate-x-1/2 p-1 cursor-pointer touch-manipulation"
                    aria-label={`View events for day ${day}`}
                    type="button"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-600" />
                  </button>
                )}
              </div>
            ) : (
              <div key={index} />
            )
          })}
        </div>
      </div>

      {/* Event Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        {selectedEvent && (() => {
          const eventType = selectedEvent.eventType as EventType | undefined
          return (
            <EventModal
              title={selectedEvent.title}
              startDate={selectedEvent.startDate}
              endDate={selectedEvent.endDate}
              location={selectedEvent.location}
              category={
                eventType && typeof eventType === 'object'
                  ? {
                      title: eventType.title,
                      colorScheme: eventType.colorScheme,
                    }
                  : undefined
              }
              labels={selectedEvent.tags?.map((tag) => (tag as Tag)?.title)}
              image={selectedEvent.image}
              description={selectedEvent.description}
              organizer={selectedEvent.organizedBy}
              organizerIsLinkable={selectedEvent.organizerIsLinkable}
              organizerLinkType={selectedEvent.organizerLinkType}
              organizerVendor={selectedEvent.organizerVendor}
              organizerLink={selectedEvent.organizerLink}
              language={selectedEvent.languages?.join(', ')}
              link={selectedEvent.link}
              showCTAButton={selectedEvent.showCTAButton}
              secondLink={selectedEvent.secondLink}
              showSecondCTAButton={selectedEvent.showSecondCTAButton}
              isRecommended={selectedEvent.isRecommended ?? undefined}
              recommendedBy={selectedEvent.recommendedBy as AuthorData[]}
              recommendedWhy={selectedEvent.isRecommended ? selectedEvent.recommendedWhy : undefined}
              longRecommendation={selectedEvent.isRecommended ? selectedEvent.longRecommendation : undefined}
              locale={locale}
            />
          )
        })()}
      </Dialog>
    </div>
  )
}
